
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import MessageRenderer from './MessageRenderer.vue';
import { ChatMessage } from './AgentChatbox.vue';
import { parseMarkdown } from '@/utils/markdownParser';
import { EventBus } from '@/main';

interface ConversationStarter {
  id: string;
  title: string;
  description: string;
  text: string;
  icon: string;
  iconColor: string;
}

@Component({
  components: {
    MessageRenderer,
  },
})
export default class ChatConversation extends Vue {
  @Prop({ default: () => [] })
  public messages!: ChatMessage[];

  @Prop({ default: false })
  public loading!: boolean;

  @Prop({ default: null })
  public selectedAgent!: any;

  @Prop({ default: false })
  public isContinued!: boolean;

  public inputMessage: string = '';

  public conversationStarters: ConversationStarter[] = [
    
  ];

  public sendMessage() {
    if (this.inputMessage.trim()) {
      this.$emit('send-message', this.inputMessage.trim());
      this.inputMessage = '';
      this.$emit('input-changed', '');
    }
  }

  public handleSwitchAgent(agentId: string) {
    // Emit event to parent to switch agent
    this.$emit('switch-agent', agentId);
  }

  public handleOpenAgentsTab() {
    // Emit event to parent to open agents tab
    this.$emit('open-agents-tab');
  }

  public createNewChat() {
    // Emit event to parent to create a new chat
    this.$emit('create-new-chat');
  }

  public handleEnter(event: KeyboardEvent) {
    if (!event.shiftKey) {
      this.sendMessage();
    }
  }

  public onInputChange() {
    this.$emit('input-changed', this.inputMessage);
  }

  public selectFeaturedAgent() {
    this.$emit('select-agent', {
      id: 'work-organizer',
      name: 'Work Organizer',
      description: 'Quickly find, update, and manage tasks in your project.',
    });
  }

  public formatTime(date: Date): string {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  public formatMessage(content: string): string {
    return parseMarkdown(content);
  }

  /**
   * Check if a message contains a live chat button
   */
  public isLiveChatButton(message: ChatMessage): boolean {
    return !!(
      message.structuredContent &&
      message.structuredContent.length > 0 &&
      message.structuredContent[0].type === 'live_chat_button'
    );
  }

  /**
   * Get the button text from message metadata
   */
  public getLiveChatButtonText(message: ChatMessage): string {
    if (this.isLiveChatButton(message)) {
      return message.structuredContent![0].buttonText || 'Connect to Live Support';
    }
    return 'Connect to Live Support';
  }

  /**
   * Get the message to display above the live chat button
   */
  public getLiveChatButtonMessage(message: ChatMessage): string {
    if (this.isLiveChatButton(message)) {
      const metadata = message.structuredContent![0].metadata || {};
      const ticketCreated = metadata.ticket_created;

      if (ticketCreated === true) {
        // Ticket was created (customer service flow)
        return 'Your support ticket has been created. You can continue chatting here or connect with our live support team.';
      } else {
        // No ticket created (report builder, purchase order, etc.)
        return 'I can connect you to our support team. You can continue chatting here or click the button below when you\'re ready.';
      }
    }
    return 'I can connect you to our support team.';
  }

  /**
   * Get the button description from message metadata
   */
  public getLiveChatButtonDescription(message: ChatMessage): string {
    if (this.isLiveChatButton(message)) {
      return message.structuredContent![0].buttonDescription || 'Click here when you\'re ready to connect with our support team';
    }
    return 'Click here when you\'re ready to connect with our support team';
  }

  /**
   * Handle live chat button click
   */
  public handleLiveChatButtonClick(message: ChatMessage) {
    if (this.isLiveChatButton(message)) {
      const buttonData = message.structuredContent![0];

      // Prepare handoff data
      const handoffData = {
        agentId: 'customer-service',
        agentName: 'Customer Service',
        conversationId: buttonData.metadata?.ticket_key || 'unknown',
        metadata: buttonData.metadata || {},
        timestamp: new Date().toISOString(),
      };

      console.log('[ChatConversation] Live chat button clicked', handoffData);

      // Open live chat directly without delay since user clicked the button
      this.openLiveChat(handoffData);
    }
  }

  public created() {
    // Listen for handoff events from AgentChatbox
    EventBus.$on('open-crisp-for-handoff', this.handleHandoffRequest);
  }

  public beforeDestroy() {
    // Clean up event listener
    EventBus.$off('open-crisp-for-handoff', this.handleHandoffRequest);
  }

  /**
   * Handle handoff request from AI agent
   */
  public async handleHandoffRequest(handoffData: any) {

    // Wait 2 seconds before opening Crisp (gives user time to see the connecting message)
    await new Promise(resolve => setTimeout(resolve, 3000));

    this.openLiveChat(handoffData);
  }

  public openLiveChat(handoffData?: any) {

    // Open Crisp chat widget
    if ((window as any).$crisp) {
      // Show and open Crisp
      (window as any).$crisp.push(['do', 'chat:show']);
      (window as any).$crisp.push(['do', 'chat:open']);

      // If this is a handoff from AI agent, add context
      if (handoffData) {
        // Add session data for support team context
        (window as any).$crisp.push(['set', 'session:data', [[
          ['ai_handoff', 'true'],
          ['agent_id', handoffData.agentId],
          ['agent_name', handoffData.agentName],
          ['conversation_id', handoffData.conversationId],
          ['handoff_timestamp', handoffData.timestamp],
          ['handoff_reason', handoffData.metadata?.handoff_reason || 'user_request'],
        ]]]);
      }

      // No notification needed - the in-chat message already shows the handoff is happening

      // Emit event to notify FAB that handoff occurred (for session-based tracking)
      if (handoffData) {
        EventBus.$emit('handoff-completed', {
          conversationId: handoffData.conversationId,
          timestamp: handoffData.timestamp,
          agentId: handoffData.agentId,
          agentName: handoffData.agentName,
        });
      }
    } else {
      // Fallback if Crisp is not loaded
      EventBus.$emit('show-snackbar', {
        message: 'Live chat is not available at the moment.',
        color: 'error',
        timeout: 5000,
      });
    }

    // Close the agent chatbox
    this.$emit('close-chatbox');
  }

  public mounted() {
    // Scroll to bottom on mount if there are messages
    if (this.messages.length > 0) {
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    }
  }

  @Watch('messages')
  public onMessagesChanged() {
    this.$nextTick(() => {
      this.scrollToBottom();
    });
  }

  private scrollToBottom() {
    const messagesList = this.$refs.messagesList as HTMLElement;
    if (messagesList) {
      messagesList.scrollTop = messagesList.scrollHeight;
    }
  }
}
